/* Scratch-card email + phone capture popup (ported from the Framer ScratchCard).
   Flow: pick -> scratch -> email -> sms -> done. Opens 3s after first session visit
   (or immediately with ?scratch=1). Email + phone post to the existing Supabase fns.

   Functional hardening (vs the Framer original):
   1. Suppress logged-in customers + visitors already identified in Klaviyo (__kla_id cookie).
   2. Funnel analytics events at every step -> Klaviyo / GA4 / Meta.
   3. Bot tripwire: honeypot field + time-trap (no Cloudflare). Robust rate-limit /
      disposable-domain blocking belongs on the Supabase function (see README note).
   4. Phone auto-formats (xxx-xxx-xxxx, cursor preserved) + validated to E.164 before submit.
   5. Email typo correction ("did you mean …@gmail.com?") + format check. */
(function () {
  var SESSION_KEY = 'bs-scratch-shown';
  var SUBMITTED_KEY = 'bs-scratch-submitted';
  var EMAIL_ENDPOINT = 'https://aaffxgkjygylwfjuwvnd.supabase.co/functions/v1/klaviyo-event';
  var PHONE_ENDPOINT = 'https://aaffxgkjygylwfjuwvnd.supabase.co/functions/v1/smart-function';
  var POPUP_SOURCE   = 'POPUP_scratch';   // Klaviyo profile tag — mirrors the opt-in popup's popup_source
  var OPEN_DELAY_MS = 3000;     // delay before auto-open on a fresh session
  var MIN_FILL_MS = 1500;       // a submit sooner than this after open = bot (the real flow
                                // requires pick + scratch + typing, which always takes longer)

  var EMAIL_TYPOS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmaill.com': 'gmail.com',
    'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmail.om': 'gmail.com',
    'gnail.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmail.colm': 'gmail.com',
    'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahoo.co': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'yhaoo.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.co': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'hotmali.com': 'hotmail.com', 'hotmil.com': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outlook.co': 'outlook.com', 'outloo.com': 'outlook.com', 'outlook.con': 'outlook.com',
    'iclod.com': 'icloud.com', 'icloud.co': 'icloud.com', 'icloud.con': 'icloud.com',
    'live.co': 'live.com', 'aol.co': 'aol.com'
  };

  function safeGet(store, key) { try { return store.getItem(key); } catch (e) { return null; } }
  function safeSet(store, key, val) { try { store.setItem(key, val); } catch (e) {} }

  // Dedup id shared by the Meta web pixel + Conversions API event (set in GTM/Stape).
  function genId() {
    try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (e) {}
    return 'sc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  // Already a known Klaviyo subscriber? (reads Klaviyo's own __kla_id identity cookie)
  function isKnownSubscriber() {
    try {
      var m = document.cookie.match(/__kla_id=([^;]+)/);
      if (m) {
        var data = JSON.parse(atob(decodeURIComponent(m[1]).split('.')[0]));
        if (data && data.$email) return true;
      }
    } catch (e) {}
    return false;
  }

  function emailTypoSuggestion(email) {
    var at = email.lastIndexOf('@');
    if (at < 0) return null;
    var domain = email.slice(at + 1).toLowerCase();
    return EMAIL_TYPOS[domain] || null;
  }
  function isValidEmail(email) { return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email); }

  function normalizePhone(raw) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    return null;
  }

  function initScratchPopup(overlay) {
    if (!overlay || overlay.dataset.scInit) return;
    overlay.dataset.scInit = '1';

    var card = overlay.querySelector('[data-sc-card]');
    var product = overlay.getAttribute('data-sc-product') || 'shake';
    var loggedIn = overlay.getAttribute('data-sc-logged-in') === 'true';
    var email = '';
    var leadPhone = '';
    var picked = false;
    var smsVerified = false;
    var opened = false;
    var openedAt = 0;
    var currentStep = 'pick';
    var typoWarnedFor = null;

    var screens = {};
    overlay.querySelectorAll('[data-sc-screen]').forEach(function (el) {
      screens[el.getAttribute('data-sc-screen')] = el;
    });

    // ── Funnel analytics ──
    // Klaviyo gets named metrics directly (its onsite JS is separate from GTM). Everything
    // else rides the theme's dataLayer -> GTM -> Stape pipeline (GA4 + Meta CAPI), same as
    // the cart drawer — we don't call gtag/fbq directly (GTM owns those tags, double-fire risk).
    function track(name, props) {
      props = props || {};
      props.product = product;
      try {
        if (window.klaviyo && window.klaviyo.push) window.klaviyo.push(['track', name, props]);
        else if (window._learnq && window._learnq.push) window._learnq.push(['track', name, props]);
      } catch (e) {}
      try {
        if (window.dataLayer) {
          var p = { event: 'scratch_popup', sc_event: name };
          for (var k in props) { if (props.hasOwnProperty(k)) p[k] = props[k]; }
          window.dataLayer.push(p);
        }
      } catch (e) {}
    }

    // Signup (email or SMS) -> the dataLayer as a `complete_registration` event, which GTM/Stape
    // maps to the Meta CompleteRegistration standard event (NOT Lead — the user reserves Lead for
    // something else; CompleteRegistration is the retargeting-audience signal here). Carries a dedup
    // event_id + the email/phone match keys for advanced matching. Klaviyo gets a named metric too.
    function pushRegistration(channel) {
      var ud = { email: email };
      if (channel === 'sms' && leadPhone) ud.phone = leadPhone;
      var klavName = 'Scratch Popup ' + (channel === 'sms' ? 'SMS' : 'Email') + ' Submitted';
      try {
        if (window.klaviyo && window.klaviyo.push) window.klaviyo.push(['track', klavName, { product: product, channel: channel }]);
        else if (window._learnq && window._learnq.push) window._learnq.push(['track', klavName, { product: product }]);
      } catch (e) {}
      try {
        if (window.dataLayer) window.dataLayer.push({
          event: 'complete_registration',
          signup_channel: channel,        // 'email' | 'sms'
          event_id: genId(),              // map to the Meta pixel + CAPI tags in GTM for dedup
          product: product,
          user_data: ud                   // raw email (+ phone) for Meta advanced matching — Stape hashes
        });
      } catch (e) {}
      // AppLovin (Axon) gets the same signup as `sign_up`. Its Shopify app pixel only sees
      // Shopify's own commerce events, so a popup capture never reaches AppLovin otherwise.
      // Commerce events are deliberately NOT mirrored — see snippets/biome-applovin.liquid.
      if (window.biomeAxon) window.biomeAxon('sign_up', { method: channel, user_data: ud });
    }

    function showStep(step) {
      currentStep = step;
      Object.keys(screens).forEach(function (k) { screens[k].hidden = (k !== step); });
      if (step === 'scratch') initCanvas();
      if (step === 'done') setDoneContent();
      if (card) card.scrollTop = 0;
    }

    // Bot tripwire: honeypot filled, or a submit faster than a human could reach this form.
    function isBot(form) {
      var hp = form.querySelector('[data-sc-hp]');
      if (hp && hp.value) return true;
      if (Date.now() - openedAt < MIN_FILL_MS) return true;
      return false;
    }

    // ── Open / close ──
    // The popup stylesheet loads async (media=print flip in the snippet) so it can't
    // render-block the PDP. Don't open unstyled: wait for the flip, but never more
    // than ~4s past the first attempt so a blocked stylesheet can't strand the popup.
    var cssDeadline = 0;
    function cssPending() {
      var l = document.querySelector('link[href*="biome-scratch-popup"][media="print"]');
      if (!l) return false;
      if (!cssDeadline) cssDeadline = Date.now() + 4000;
      return Date.now() < cssDeadline;
    }
    function open() {
      if (opened) return;
      if (cssPending()) { setTimeout(open, 120); return; }
      opened = true;
      openedAt = Date.now();
      overlay.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      track('Scratch Popup Viewed', { source: 'scratch_popup' });
    }
    function close() {
      if (!overlay.hidden) track('Scratch Popup Closed', { at_step: currentStep });
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
    }

    overlay.querySelectorAll('[data-sc-close]').forEach(function (btn) { btn.addEventListener('click', close); });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) { close(); return; }
      var fix = e.target.closest && e.target.closest('[data-sc-typo-fix]');
      if (fix) {
        var emailInput = overlay.querySelector('[data-sc-email]');
        if (emailInput) { emailInput.value = fix.getAttribute('data-fix'); typoWarnedFor = null; }
        var ef = overlay.querySelector('[data-sc-email-form]');
        if (ef && ef.requestSubmit) ef.requestSubmit();
      }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) close(); });

    // ── Screen 1: pick ──
    var cardEls = Array.prototype.slice.call(overlay.querySelectorAll('[data-sc-card-pick]'));
    function pick(chosen) {
      if (picked) return;
      picked = true;
      var idx = cardEls.indexOf(chosen);
      track('Scratch Popup Card Picked', { card_index: idx });
      cardEls.forEach(function (c) { c.classList.add(c === chosen ? 'is-picked' : 'is-dismissed'); });
      setTimeout(function () { showStep('scratch'); }, 450);
    }
    cardEls.forEach(function (c) { c.addEventListener('click', function () { pick(c); }); });
    var pickCta = overlay.querySelector('[data-sc-pick-cta]');
    if (pickCta) pickCta.addEventListener('click', function () { pick(cardEls[1] || cardEls[0]); });

    // ── Screen 2: scratch (canvas) ──
    var canvas = overlay.querySelector('[data-sc-canvas]');
    var progressFill = overlay.querySelector('[data-sc-progress-fill]');
    var progressLabel = overlay.querySelector('[data-sc-progress-label]');
    var revealNow = overlay.querySelector('[data-sc-reveal-now]');
    var drawing = false, lastPos = null, moveCount = 0, advanced = false, canvasReady = false;

    function initCanvas() {
      if (!canvas) return;
      advanced = false; moveCount = 0; canvasReady = true;
      canvas.classList.remove('is-revealed');
      setProgress(0);
      var ctx = canvas.getContext('2d');
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      var grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, '#C9A96E'); grad.addColorStop(0.35, '#E8CFA0');
      grad.addColorStop(0.65, '#A68D60'); grad.addColorStop(1, '#8D745D');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(251,248,243,0.18)';
      for (var x = 0; x < canvas.width; x += 6) {
        for (var y = 0; y < canvas.height; y += 6) {
          ctx.beginPath(); ctx.arc(x + 1, y + 1, 0.8, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.font = '600 ' + Math.round(canvas.width * 0.048) + 'px Gilroy, Manrope, sans-serif';
      ctx.fillStyle = 'rgba(251,248,243,0.5)';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('BIOME SECRET', canvas.width / 2, canvas.height * 0.18);
    }

    function setProgress(p) {
      var pct = Math.round(p * 100);
      if (progressFill) progressFill.style.width = Math.min(100, pct) + '%';
      if (progressLabel) progressLabel.textContent = (pct < 60 ? 'Keep scratching · ' : 'Almost there · ') + pct + '% revealed';
    }
    function canvasPos(e) {
      var rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
      };
    }
    function checkProgress() {
      if (advanced) return;
      var ctx = canvas.getContext('2d');
      var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      var transparent = 0;
      for (var i = 3; i < data.length; i += 4) { if (data[i] < 128) transparent++; }
      var p = transparent / (canvas.width * canvas.height);
      setProgress(p);
      if (p >= 0.6) reveal('scratched');
    }
    function reveal(method) {
      if (advanced) return;
      advanced = true;
      track('Scratch Popup Scratch Completed', { method: method || 'scratched' });
      canvas.classList.add('is-revealed');
      setTimeout(function () { showStep('email'); }, 450);
    }
    if (canvas) {
      canvas.addEventListener('pointerdown', function (e) {
        if (!canvasReady) return;
        drawing = true; lastPos = canvasPos(e);
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!drawing) return;
        var ctx = canvas.getContext('2d');
        var pos = canvasPos(e);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath(); ctx.arc(pos.x, pos.y, 22, 0, Math.PI * 2); ctx.fill();
        if (lastPos) {
          ctx.beginPath(); ctx.moveTo(lastPos.x, lastPos.y); ctx.lineTo(pos.x, pos.y);
          ctx.lineWidth = 44; ctx.lineCap = 'round'; ctx.stroke();
        }
        lastPos = pos; moveCount++;
        if (moveCount % 6 === 0) checkProgress();
      });
      var stop = function () { drawing = false; lastPos = null; };
      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointerleave', stop);
      canvas.addEventListener('pointercancel', stop);
    }
    if (revealNow) revealNow.addEventListener('click', function () { reveal('reveal_now'); });

    // ── Screen 3: email ──
    var emailForm = overlay.querySelector('[data-sc-email-form]');
    var emailInput = overlay.querySelector('[data-sc-email]');
    var emailError = overlay.querySelector('[data-sc-email-error]');
    var emailSubmit = overlay.querySelector('[data-sc-email-submit]');
    function showEmailError(html, isHtml) {
      if (isHtml) emailError.innerHTML = html; else emailError.textContent = html;
      emailError.hidden = false;
    }
    if (emailForm) {
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        email = (emailInput.value || '').trim();
        if (!email) return;
        if (isBot(emailForm)) return;                 // honeypot / time-trap: ignore bots
        if (!isValidEmail(email)) { showEmailError('Please enter a valid email address.'); return; }
        var sugg = emailTypoSuggestion(email);
        if (sugg && typoWarnedFor !== email) {        // typo nudge (submit again to override)
          typoWarnedFor = email;
          var fixed = email.slice(0, email.lastIndexOf('@') + 1) + sugg;
          showEmailError('Did you mean <button type="button" class="sc-error-fix" data-sc-typo-fix data-fix="' + fixed + '">' + fixed + '</button>?', true);
          return;
        }
        emailError.hidden = true;
        emailSubmit.disabled = true; emailSubmit.textContent = 'Sending…';
        fetch(EMAIL_ENDPOINT + '?product=' + encodeURIComponent(product) + '&isFortuneWheel=true&source=' + encodeURIComponent(POPUP_SOURCE), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email, source: POPUP_SOURCE })
        }).then(function (res) {
          if (!res.ok) throw new Error('Failed to submit email');
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
            var idProps = { email: email, popup_source: POPUP_SOURCE };
            idProps[POPUP_SOURCE] = true;            // POPUP_scratch: true (segment tag)
            try { window.klaviyo.identify(idProps); } catch (_) {}
          }
          pushRegistration('email');
          showStep('sms');
        }).catch(function (err) {
          track('Scratch Popup Email Failed', { error: String((err && err.message) || err) });
          showEmailError((err && err.message) || 'An error occurred');
        }).then(function () {
          emailSubmit.disabled = false; emailSubmit.textContent = 'Claim my reward';
        });
      });
    }

    // ── Screen 4: sms ──
    var smsForm = overlay.querySelector('[data-sc-sms-form]');
    var phoneInput = overlay.querySelector('[data-sc-phone]');
    var phoneError = overlay.querySelector('[data-sc-phone-error]');
    var phoneSubmit = overlay.querySelector('[data-sc-phone-submit]');
    var skipSms = overlay.querySelector('[data-sc-skip-sms]');

    // Auto-format US phone as xxx-xxx-xxxx, preserving the caret position.
    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        var oldValue = phoneInput.value;
        var oldCursor = phoneInput.selectionStart;
        var digitsBeforeCursor = oldValue.slice(0, oldCursor).replace(/\D/g, '').length;
        var digits = oldValue.replace(/\D/g, '').slice(0, 10);
        var formatted = digits;
        if (digits.length > 6) formatted = digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
        else if (digits.length > 3) formatted = digits.slice(0, 3) + '-' + digits.slice(3);
        phoneInput.value = formatted;
        var newCursor = 0, seen = 0;
        while (newCursor < formatted.length && seen < digitsBeforeCursor) {
          if (/\d/.test(formatted[newCursor])) seen++;
          newCursor++;
        }
        try { phoneInput.setSelectionRange(newCursor, newCursor); } catch (_) {}
      });
    }
    if (smsForm) {
      smsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (isBot(smsForm)) return;
        var phone = normalizePhone(phoneInput.value);
        if (!phone) {
          track('Scratch Popup SMS Failed', { error: 'invalid_format' });
          phoneError.textContent = 'Please enter a valid US phone number.'; phoneError.hidden = false;
          return;
        }
        phoneError.hidden = true;
        phoneSubmit.disabled = true; phoneSubmit.textContent = 'Please wait…';
        fetch(PHONE_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: phone, email: email, source: POPUP_SOURCE })
        }).then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error((data && data.error) || 'An error occurred');
            safeSet(localStorage, SUBMITTED_KEY, 'true');
            leadPhone = phone;
            pushRegistration('sms');
            smsVerified = true;
            showStep('done');
          });
        }).catch(function (err) {
          track('Scratch Popup SMS Failed', { error: String((err && err.message) || err) });
          phoneError.textContent = (err && err.message) || 'An error occurred'; phoneError.hidden = false;
        }).then(function () {
          phoneSubmit.disabled = false; phoneSubmit.textContent = 'Text me my 10% code';
        });
      });
    }
    if (skipSms) {
      skipSms.addEventListener('click', function () {
        safeSet(localStorage, SUBMITTED_KEY, 'true');
        track('Scratch Popup SMS Skipped', {});
        smsVerified = false;
        showStep('done');
      });
    }

    // ── Screen 5: done ──
    function setDoneContent() {
      var h = overlay.querySelector('[data-sc-done-headline]');
      var s = overlay.querySelector('[data-sc-done-sub]');
      var pillEmail = overlay.querySelector('[data-sc-pill="email"]');
      var pillSms = overlay.querySelector('[data-sc-pill="sms"]');
      if (h) h.textContent = smsVerified ? 'Check your phone' : 'Check your email';
      if (s) s.textContent = smsVerified
        ? "Your 10% discount code is on its way — we'll text it to you within a few hours."
        : "Your $5 coupon is on its way to your inbox. Happy shopping!";
      if (pillEmail) pillEmail.hidden = smsVerified;
      if (pillSms) pillSms.hidden = !smsVerified;
    }

    // ── Open schedule (suppression) ──
    var force = /[?&](scratch|sc_test)=1/.test(window.location.search);
    if (force) {
      open();
    } else if (
      !loggedIn &&
      !safeGet(localStorage, SUBMITTED_KEY) &&
      !safeGet(sessionStorage, SESSION_KEY) &&
      !isKnownSubscriber()
    ) {
      setTimeout(function () {
        safeSet(sessionStorage, SESSION_KEY, 'true');
        open();
      }, OPEN_DELAY_MS);
    }
  }

  function boot() {
    var overlay = document.querySelector('[data-sc-overlay]');
    if (overlay) initScratchPopup(overlay);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
